import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { labelOf, colorOf } from "@/lib/statuses";
import { Plus, AlertTriangle, Edit3, Trash2, Upload } from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid,
} from "recharts";

const TABS = [
  ["oversikt", "Oversikt"],
  ["bestillinger", "Bestillinger"],
  ["produkter", "Produkter"],
  ["verksteder", "Verksteder"],
  ["fakturaer", "Fakturaer"],
  ["rabattkoder", "Rabattkoder"],
  ["soknader", "Verksted-søknader"],
  ["kunder", "Kunder"],
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("oversikt");

  return (
    <div className="tk-container py-16" data-testid="admin-dashboard">
      <p className="tk-overline mb-3">Admin</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-10">Driftspanel</h1>

      <div className="flex gap-2 mb-10 flex-wrap" data-testid="admin-tab-bar">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-md text-sm border ${tab === k ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
            data-testid={`admin-tab-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "oversikt" && <OversiktTab />}
      {tab === "bestillinger" && <BestillingerTab />}
      {tab === "produkter" && <ProdukterTab />}
      {tab === "verksteder" && <VerkstederTab />}
      {tab === "fakturaer" && <FakturaerTab />}
      {tab === "rabattkoder" && <RabattkoderTab />}
      {tab === "soknader" && <SoknaderTab />}
      {tab === "kunder" && <KunderTab />}
    </div>
  );
}

// ============================================================
//  OVERSIKT
// ============================================================
function OversiktTab() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStock, setLowStock] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/charts/revenue").then((r) => setRevenue(r.data));
    api.get("/admin/charts/categories").then((r) => setCategories(r.data));
    api.get("/admin/low-stock").then((r) => setLowStock(r.data));
  }, []);

  return (
    <div data-testid="admin-oversikt">
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="tk-card">
          <p className="tk-overline mb-3">Daglig omsetning (siste 30 dager)</p>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={revenue} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.slice(5)}
                  tick={{ fontSize: 11, fill: "#64748B" }}
                  stroke="#CBD5E1"
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} stroke="#CBD5E1" />
                <Tooltip
                  formatter={(v) => [`kr ${Number(v).toFixed(0)}`, "Omsetning"]}
                  labelFormatter={(d) => d}
                  contentStyle={{ borderRadius: 6, fontSize: 12 }}
                />
                <Line type="monotone" dataKey="revenue" stroke="#0B1C30" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3">Bestillinger per kategori</p>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={categories} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#64748B" }} stroke="#CBD5E1" />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} stroke="#CBD5E1" />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="count" fill="#4A7C59" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="tk-card mb-8 border-l-4 border-amber-400" data-testid="low-stock-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-1" />
            <div className="flex-1">
              <p className="font-semibold">Lav lagerbeholdning</p>
              <p className="text-xs text-slate-500 mb-3">Disse produktene har 3 eller færre enheter igjen.</p>
              <ul className="text-sm space-y-1">
                {lowStock.map((p) => (
                  <li key={p.id} className="flex justify-between" data-testid={`low-stock-${p.id}`}>
                    <span>{p.name} <span className="text-slate-400">({p.category})</span></span>
                    <span className="font-mono text-amber-700">{p.stock_quantity} igjen</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="admin-stats">
          <Stat label="Totale bestillinger" value={stats.total_orders} />
          <Stat label="Betalte bestillinger" value={stats.paid_orders} />
          <Stat label="Omsetning (NOK)" value={`kr ${stats.revenue_nok.toFixed(0)}`} />
          <Stat label="Produkter" value={stats.products} />
          <Stat label="Aktive verksteder" value={stats.active_fixers} />
          <Stat label="Kunder" value={stats.customers} />
        </div>
      )}
    </div>
  );
}

// ============================================================
//  BESTILLINGER  (3 sub-tabs)
// ============================================================
function BestillingerTab() {
  const [sub, setSub] = useState("aktive");
  const [orders, setOrders] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const payout = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}`);
      toast.success("Markert utbetalt");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const PAID_OUT = "paid_out";
  const FINAL_STATUSES = new Set(["completed", "sent_back", "declined_returning"]);

  const aktive = orders.filter(
    (o) => o.payment_status === "paid" && o.status !== PAID_OUT && !FINAL_STATUSES.has(o.status),
  );
  const venter = orders.filter(
    (o) => o.payment_status === "paid" && FINAL_STATUSES.has(o.status) && o.status !== PAID_OUT,
  );
  const historikk = orders.filter((o) => o.status === PAID_OUT);

  const SUB = [
    ["aktive", `Aktive (${aktive.length})`],
    ["venter", `Venter på utbetaling (${venter.length})`],
    ["historikk", `Historikk (${historikk.length})`],
  ];

  return (
    <div data-testid="admin-bestillinger">
      <div className="flex gap-2 mb-6 flex-wrap">
        {SUB.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-md text-xs border ${sub === k ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
            data-testid={`bestillinger-sub-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      <OrdersTable
        orders={sub === "aktive" ? aktive : sub === "venter" ? venter : historikk}
        showPayout={sub === "venter"}
        onPayout={payout}
      />
    </div>
  );
}

function OrdersTable({ orders, showPayout, onPayout }) {
  if (orders.length === 0) return <p className="text-slate-500">Ingen bestillinger.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-orders-table">
        <thead>
          <tr className="text-left border-b border-[#E2E8F0]">
            <th className="py-3 pr-4 font-medium text-slate-500">Ordre</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Kunde</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Reparasjon</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Verksted</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Levering</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Total</th>
            <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
            {showPayout && <th className="py-3 pr-4 font-medium text-slate-500"></th>}
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-[#E2E8F0]/70 hover:bg-slate-50" data-testid={`admin-order-${o.id}`}>
              <td className="py-3 pr-4 font-mono text-xs">
                <Link to={`/admin/bestilling/${o.id}`} className="text-slate-400 hover:text-[#0B1C30]" data-testid={`admin-order-link-${o.id}`}>
                  #{o.id.slice(0, 8)} ↗
                </Link>
              </td>
              <td className="py-3 pr-4">{o.customer_name}</td>
              <td className="py-3 pr-4">{o.product_name} <span className="text-slate-400">/ {o.service_name}</span></td>
              <td className="py-3 pr-4">{o.fixer_display_name}</td>
              <td className="py-3 pr-4 text-xs">{o.delivery_method === "shipping" ? "Bring" : "Drop-off"}</td>
              <td className="py-3 pr-4 font-medium">kr {Number(o.total_price).toFixed(0)}</td>
              <td className="py-3 pr-4"><span className={`text-xs px-2 py-1 rounded-md ${colorOf(o.status)}`}>{labelOf(o.status)}</span></td>
              {showPayout && (
                <td className="py-3 pr-4">
                  <button onClick={() => onPayout(o.id)} className="tk-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`payout-${o.id}`}>
                    Marker utbetalt
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
//  PRODUKTER
// ============================================================
function ProdukterTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-produkter">
      <button onClick={() => setEditing("new")} className="tk-btn-primary mb-6" data-testid="open-new-product">
        <Plus size={16} /> Nytt produkt
      </button>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead>
            <tr className="text-left border-b border-[#E2E8F0]">
              <th className="py-3 pr-4 font-medium text-slate-500">Navn</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Kategori</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Pris</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Lager</th>
              <th className="py-3 pr-4 font-medium text-slate-500"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const low = p.stock_quantity <= 3;
              return (
                <tr
                  key={p.id}
                  className={`border-b border-[#E2E8F0]/70 cursor-pointer hover:bg-slate-50 ${low ? "bg-amber-50/40" : ""}`}
                  onClick={() => setEditing(p)}
                  data-testid={`admin-product-${p.id}`}
                >
                  <td className="py-3 pr-4 flex items-center gap-3">
                    {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-cover border border-[#E2E8F0]" />}
                    <span>{p.name}</span>
                  </td>
                  <td className="py-3 pr-4">{p.category}</td>
                  <td className="py-3 pr-4">kr {Number(p.price).toFixed(0)}</td>
                  <td className="py-3 pr-4">
                    <span className={low ? "text-amber-700 font-semibold inline-flex items-center gap-1" : ""}>
                      {low && <AlertTriangle size={12} />} {p.stock_quantity}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-400 text-xs"><Edit3 size={14} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductEditDrawer
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ProductEditDrawer({ product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    name: product?.name || "",
    category: product?.category || "iPhone",
    compatible_models: (product?.compatible_models || []).join(", "),
    price: product?.price ?? "",
    stock_quantity: product?.stock_quantity ?? "",
    image: product?.image || "",
    description: product?.description || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Bilde må være under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock_quantity: parseInt(form.stock_quantity || "0", 10),
      compatible_models: form.compatible_models.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.patch(`/admin/products/${product.id}`, payload);
      toast.success(isNew ? "Produkt lagt til" : "Produkt oppdatert");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Slett produkt?")) return;
    await api.delete(`/admin/products/${product.id}`);
    toast.success("Produkt slettet");
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="product-edit-drawer">
        <p className="tk-overline mb-2">{isNew ? "Nytt produkt" : "Rediger produkt"}</p>
        <h2 className="text-2xl font-semibold mb-6">{form.name || "—"}</h2>
        <form onSubmit={save} className="space-y-3">
          <input className="tk-input" placeholder="Navn" required value={form.name} onChange={set("name")} data-testid="prod-name" />
          <select className="tk-input" value={form.category} onChange={set("category")} data-testid="prod-category">
            {["iPhone", "iPad", "MacBook", "iMac"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className="tk-input" placeholder="Pris (NOK)" type="number" step="0.01" required value={form.price} onChange={set("price")} data-testid="prod-price" />
          <input className="tk-input" placeholder="Lagerantall" type="number" value={form.stock_quantity} onChange={set("stock_quantity")} data-testid="prod-stock" />
          <input className="tk-input" placeholder="Kompatible modeller (kommaseparert)" value={form.compatible_models} onChange={set("compatible_models")} data-testid="prod-models" />
          <textarea className="tk-input min-h-[80px]" placeholder="Beskrivelse" value={form.description} onChange={set("description")} data-testid="prod-description" />

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Produktbilde</p>
            {form.image && <img src={form.image} alt="" className="w-32 h-32 rounded object-cover border border-[#E2E8F0] mb-2" />}
            <label className="tk-btn-secondary cursor-pointer inline-flex !py-2 !px-3 text-sm" data-testid="prod-image-upload-label">
              <Upload size={14} /> {form.image ? "Bytt bilde" : "Last opp bilde"}
              <input type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="prod-image-upload" />
            </label>
          </div>

          <div className="flex justify-between pt-4">
            <button type="button" onClick={onClose} className="tk-btn-secondary" data-testid="prod-cancel">Avbryt</button>
            <div className="flex gap-2">
              {!isNew && (
                <button type="button" onClick={remove} className="text-sm text-red-600 hover:underline px-3" data-testid="prod-delete">
                  <Trash2 size={14} className="inline" /> Slett
                </button>
              )}
              <button type="submit" disabled={busy} className="tk-btn-primary" data-testid="prod-save">
                {busy ? "Lagrer …" : "Lagre"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
//  VERKSTEDER
// ============================================================
function VerkstederTab() {
  const [fixers, setFixers] = useState([]);
  useEffect(() => { api.get("/admin/fixers").then((r) => setFixers(r.data)); }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-fixers-list">
      {fixers.map((f) => (
        <Link
          key={f.id}
          to={`/admin/verksted/${f.id}`}
          className="tk-card tk-card-interactive block"
          data-testid={`admin-fixer-${f.id}`}
        >
          <p className="tk-overline mb-2 text-[#4A7C59]">{f.postal_code}</p>
          <h3 className="font-semibold text-lg leading-snug">{f.display_name}</h3>
          <p className="text-sm text-slate-500 mt-1">{f.business_name}</p>
          <p className="text-xs text-slate-500 mt-2">{f.address}, {f.postal_code}</p>
          <span className={`text-xs px-2 py-1 rounded-md mt-3 inline-block ${f.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {f.active ? "Aktiv" : "Inaktiv"}
          </span>
        </Link>
      ))}
    </div>
  );
}

// ============================================================
//  FAKTURAER
// ============================================================
function FakturaerTab() {
  const [invoices, setInvoices] = useState([]);
  const [fixerFilter, setFixerFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => api.get("/admin/invoices").then((r) => setInvoices(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      await api.patch(`/admin/invoices/${id}/pay`);
      toast.success("Faktura markert som betalt — bekreftelse sendt til verksted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const fixerNames = [...new Set(invoices.map((i) => i.fixer_display_name))];
  const filtered = invoices.filter((i) => {
    if (fixerFilter && i.fixer_display_name !== fixerFilter) return false;
    if (from && i.created_at.slice(0, 10) < from) return false;
    if (to && i.created_at.slice(0, 10) > to) return false;
    return true;
  });

  return (
    <div data-testid="admin-fakturaer">
      <div className="flex flex-wrap gap-2 mb-6 items-end">
        <div>
          <label className="text-xs text-slate-500">Verksted</label>
          <select value={fixerFilter} onChange={(e) => setFixerFilter(e.target.value)} className="tk-input mt-1 !py-1.5 text-sm" data-testid="fak-fixer-filter">
            <option value="">Alle</option>
            {fixerNames.map((n) => <option key={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Fra dato</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="tk-input mt-1 !py-1.5 text-sm" data-testid="fak-from" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Til dato</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="tk-input mt-1 !py-1.5 text-sm" data-testid="fak-to" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-slate-500">Ingen fakturaer.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv) => <InvoiceCard key={inv.id} invoice={inv} onMarkPaid={() => markPaid(inv.id)} />)}
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ invoice, onMarkPaid }) {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    if (!open) return;
    Promise.all(
      (invoice.order_ids || []).map((id) => api.get(`/orders/${id}`).then((r) => r.data).catch(() => null)),
    ).then((data) => setOrders(data.filter(Boolean)));
  }, [open, invoice.order_ids]);

  return (
    <div className="tk-card" data-testid={`admin-invoice-${invoice.id}`}>
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <p className="font-mono text-xs text-slate-400">#{invoice.id.slice(0, 8)}</p>
          <h3 className="font-semibold">{invoice.fixer_display_name}</h3>
          <p className="text-xs text-slate-500">
            {new Date(invoice.created_at).toLocaleDateString("nb-NO")} · {(invoice.order_ids || []).length} oppdrag
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xl font-semibold">kr {Number(invoice.amount).toFixed(0)}</p>
          <span className={`text-xs px-2 py-1 rounded-md ${invoice.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {invoice.status === "paid" ? "Betalt" : "Sendt"}
          </span>
          <button onClick={() => setOpen(!open)} className="tk-btn-secondary !py-1.5 !px-3 text-xs">
            {open ? "Skjul" : "Detaljer"}
          </button>
          {invoice.status === "submitted" && (
            <button onClick={onMarkPaid} className="tk-btn-primary !py-1.5 !px-3 text-xs" data-testid={`mark-paid-${invoice.id}`}>
              Marker som betalt
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-testid={`invoice-detail-${invoice.id}`}>
          <p className="tk-overline mb-2">Oppdrag i denne fakturaen</p>
          <ul className="text-sm space-y-1">
            {orders.map((o) => (
              <li key={o.id} className="flex justify-between">
                <span>{o.product_name} · {o.service_name}</span>
                <span className="text-slate-500">kr {Number(o.service_fee).toFixed(0)}</span>
              </li>
            ))}
          </ul>
          {invoice.notes && <p className="text-xs text-slate-500 mt-3 italic">"{invoice.notes}"</p>}
          <p className="text-xs text-slate-400 mt-3">Bankkonto: {invoice.bank_account || "—"}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
//  RABATTKODER
// ============================================================
function RabattkoderTab() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", discount_percent: 10, expires_at: "", max_uses: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/promo-codes").then((r) => setCodes(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code,
        discount_percent: parseInt(form.discount_percent, 10),
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      });
      toast.success("Rabattkode opprettet");
      setForm({ code: "", discount_percent: 10, expires_at: "", max_uses: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const toggle = async (c) => {
    await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
    load();
  };

  return (
    <div data-testid="admin-rabattkoder">
      <form onSubmit={create} className="tk-card mb-8 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500">Kode</label>
          <input className="tk-input mt-1" required placeholder="LAUNCH20" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Rabatt (%)</label>
          <input className="tk-input mt-1" required type="number" min="1" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="promo-discount" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Utløper</label>
          <input className="tk-input mt-1" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expires" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Maks bruk</label>
          <input className="tk-input mt-1" type="number" placeholder="ubegrenset" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="promo-max-uses" />
        </div>
        <button type="submit" disabled={busy} className="tk-btn-primary justify-center" data-testid="promo-create-btn">
          <Plus size={14} /> Opprett
        </button>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-promo-table">
          <thead>
            <tr className="text-left border-b border-[#E2E8F0]">
              <th className="py-3 pr-4 font-medium text-slate-500">Kode</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Rabatt</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Bruk</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Utløper</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} className="border-b border-[#E2E8F0]/70" data-testid={`promo-${c.id}`}>
                <td className="py-3 pr-4 font-mono font-semibold">{c.code}</td>
                <td className="py-3 pr-4">{c.discount_percent}%</td>
                <td className="py-3 pr-4">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="py-3 pr-4">{c.expires_at || "—"}</td>
                <td className="py-3 pr-4">
                  <span className={`text-xs px-2 py-1 rounded-md ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {c.active ? "Aktiv" : "Deaktivert"}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <button onClick={() => toggle(c)} className="text-xs underline text-slate-500" data-testid={`promo-toggle-${c.id}`}>
                    {c.active ? "Deaktiver" : "Aktiver"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
//  VERKSTED-SØKNADER
// ============================================================
function SoknaderTab() {
  const [sub, setSub] = useState("pending");
  const [apps, setApps] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/fixer-applications", { params: { status: sub } }).then((r) => setApps(r.data));
  }, [sub]);
  useEffect(() => { load(); }, [load]);

  const decide = async (a, kind) => {
    try {
      await api.post(`/admin/fixer-applications/${a.id}/${kind}`, { note: "" });
      toast.success(kind === "approve" ? "Søknad godkjent — velkomstmail sendt" : "Søknad avvist — avslagsmail sendt");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-soknader">
      <div className="flex gap-2 mb-6">
        {[["pending", "Nye"], ["approved", "Godkjente"], ["rejected", "Avslåtte"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} className={`px-3 py-1.5 rounded-md text-xs border ${sub === k ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`} data-testid={`sok-sub-${k}`}>{l}</button>
        ))}
      </div>

      {apps.length === 0 ? (
        <p className="text-slate-500">Ingen søknader.</p>
      ) : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} className="tk-card" data-testid={`app-${a.id}`}>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{a.business_name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{a.contact_name} · {a.contact_email} {a.contact_phone && `· ${a.contact_phone}`}</p>
                  <p className="text-xs text-slate-500 mt-1">{a.address}, {a.postal_code}</p>
                  <p className="text-xs text-slate-500 mt-1">Spesialiseringer: {(a.specializations || []).join(", ") || "—"}</p>
                  {a.notes && <p className="text-sm italic text-slate-600 mt-2">"{a.notes}"</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString("nb-NO")}</p>
                  {a.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => decide(a, "approve")} className="tk-btn-accent !py-1.5 !px-3 text-xs" data-testid={`approve-app-${a.id}`}>Godkjenn</button>
                      <button onClick={() => decide(a, "reject")} className="tk-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`reject-app-${a.id}`}>Avslå</button>
                    </div>
                  )}
                  {a.status !== "pending" && (
                    <span className={`text-xs px-2 py-1 rounded-md mt-2 inline-block ${a.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {a.status === "approved" ? "Godkjent" : "Avslått"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
//  KUNDER
// ============================================================
function KunderTab() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!search) return customers;
    const s = search.toLowerCase();
    return customers.filter(
      (c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s),
    );
  }, [customers, search]);

  return (
    <div data-testid="admin-kunder">
      <input
        type="search"
        placeholder="Søk på navn eller e-post"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="tk-input max-w-md mb-6"
        data-testid="customer-search"
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-customers-table">
          <thead>
            <tr className="text-left border-b border-[#E2E8F0]">
              <th className="py-3 pr-4 font-medium text-slate-500">Navn</th>
              <th className="py-3 pr-4 font-medium text-slate-500">E-post</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Telefon</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Bestillinger</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Forbruk</th>
              <th className="py-3 pr-4 font-medium text-slate-500">Registrert</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-[#E2E8F0]/70 hover:bg-slate-50" data-testid={`admin-customer-${c.id}`}>
                <td className="py-3 pr-4">
                  <Link to={`/admin/kunde/${c.id}`} className="text-[#0B1C30] hover:underline" data-testid={`customer-link-${c.id}`}>
                    {c.name}
                  </Link>
                </td>
                <td className="py-3 pr-4">{c.email}</td>
                <td className="py-3 pr-4">{c.phone_number || "—"}</td>
                <td className="py-3 pr-4">{c.order_count}</td>
                <td className="py-3 pr-4 font-medium">kr {c.total_spent.toFixed(0)}</td>
                <td className="py-3 pr-4 text-slate-500">{new Date(c.created_at).toLocaleDateString("nb-NO")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="tk-card">
      <p className="tk-overline mb-2">{label}</p>
      <p className="text-3xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}
